import React, { useState, useEffect } from "react";
import axios from "axios";
import { sifarniciData } from "./SifarniciData";
import EICInput from "./EICInput";
import { fetchAvailableFiles, handleParse, handleUpload } from "./apiFileHandlers";
import CodebookTabs from "./CodebookTabs";
import DataTable from "./DataTable";
import { toast } from "react-toastify";
import "./Codebook.css";
import { BASE_URL } from "../../config";
import { getAccessToken } from "../LoginForm/Auth/auth";

function Codebook({ setOverlay }) {
    const [activeTab, setActiveTab] = useState("Šifarnici");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState("");
    const [tableData, setTableData] = useState([]);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [selectedColumn, setSelectedColumn] = useState("");
    const [eicCode, setEicCode] = useState("");
    const [mjernaMjestaData, setMjernaMjestaData] = useState([]);
    const [mjernaMjestaMjerenjaData, setMjernaMjestaMjerenjaData] = useState([]);
    const [file, setFile] = useState(null);
    const [availableFiles, setAvailableFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState("");
    const [selectedFeature, setSelectedFeature] = useState(null);

    const token = getAccessToken();
    const renderSifarniciDropdown = () => {
        return (
            <div className="codebook-dropdown-menu">
                <div className="grid-container">
                    {sifarniciData.map((sifarnik, index) => (
                        <div
                            className={`grid-item ${selectedItem === sifarnik.name ? "active" : ""}`}
                            key={index}
                            onClick={() => handleItemClick(sifarnik.name)}
                        >
                            {sifarnik.icon} <span>{sifarnik.name}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleTabClick = (tabName) => {
        if (activeTab === tabName && dropdownOpen) {
            setDropdownOpen(false);
            setActiveTab("");
        } else if (selectedItem && activeTab === tabName) {
            setSelectedItem("");
            setDropdownOpen(true);
        } else {
            setActiveTab(tabName);
            setDropdownOpen(true);
            setSelectedItem("");
            setEicCode("");
            setMjernaMjestaData([]);
            setMjernaMjestaMjerenjaData([]);
            setTableData([]);
            setFile();
        }
    };

    const fetchData = async ({
        layerName,
        setDataFunction,
        cqlFilter = null,
    }) => {
        if (cqlFilter && cqlFilter.includes("eic") && !eicCode) {
            toast.error("Unesite EIC kod!");
            return;
        }

        setOverlay({ active: true, message: "Učitavam podatke..." });

        try {
            const response = await axios.get(`${BASE_URL}/geoserver/ows`, {
                params: {
                    service: "WFS",
                    version: "1.0.0",
                    request: "GetFeature",
                    typeName: layerName,
                    outputFormat: "application/json",
                    ...(cqlFilter ? { CQL_FILTER: cqlFilter } : {}),
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const features = response.data.features.map((feature) => ({
                ...feature.properties,
            }));

            if (features.length === 0 && cqlFilter) {
                toast.info("Nema podataka za uneti filter.");
            } else if (features.length > 0 && cqlFilter) {
                setSelectedFeature(response.data.features);
            }

            setDataFunction(features);
        } catch (err) {
            toast.error("Greška prilikom preuzimanja podataka.");
        } finally {
            setOverlay({ active: false, message: "" });
        }
    };


    const handleItemClick = async (item) => {
        const selectedSifarnik = sifarniciData.find(sifarnik => sifarnik.name === item);
        if (!selectedSifarnik) return;

        setSelectedItem(selectedSifarnik.name); // ✅ ispravno poređenje

        fetchData({
            layerName: selectedSifarnik.layer,
            setDataFunction: setTableData
        });
    };


    const filteredData = tableData.filter((row) => {
        if (!searchKeyword) return true;
        if (selectedColumn) {
            return row[selectedColumn]?.toString().toLowerCase().includes(searchKeyword.toLowerCase());
        }
        return Object.values(row).some((value) =>
            value?.toString().toLowerCase().includes(searchKeyword.toLowerCase())
        );
    });

    const fetchDataByEIC = async (layerName) => {
        const cql = `eic='${eicCode}'`;

        let setDataFunction;

        if (layerName === "mjerna_mjesta") {
            setDataFunction = setMjernaMjestaData;
        } else if (layerName === "mjerna_mjesta_mjerenja") {
            setDataFunction = setMjernaMjestaMjerenjaData;
        } else {
            return;
        }

        fetchData({
            layerName,
            setDataFunction,
            cqlFilter: cql,
        });
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUploadClick = () => handleUpload(file, setOverlay, setFile);

    const handleParseClick = () => handleParse(selectedFile, setOverlay, setSelectedFile);

    const generateEICFileFromGeoServer = async () => {
        setOverlay({ active: true, message: "Generišem EIC fajl..." });

        try {
            const response = await axios.get(`${BASE_URL}/geoserver/ows`, {
                params: {
                    service: "WFS",
                    version: "1.0.0",
                    request: "GetFeature",
                    typeName: "mjerna_mjesta",
                    outputFormat: "application/json",
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const features = response.data.features || [];

            const filteredEICs = features
                .filter(f => Array.isArray(f.geometry?.coordinates) &&
                    f.geometry.coordinates[0] === 1 &&
                    f.geometry.coordinates[1] === 1 &&
                    f.properties?.eic)
                .map(f => f.properties.eic);

            if (filteredEICs.length === 0) {
                toast.info("Nema EIC kodova sa geometrijom [1, 1].");
                return;
            }


            const blob = new Blob(
                [filteredEICs.map(eic => `EIC: ${eic}`).join("\n")],
                { type: "text/plain" }
            );

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "eic_geometrija_1_1.txt";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            toast.error("Greška prilikom generisanja fajla.");
            console.error(err);
        }
        finally {
            setOverlay({ active: false, message: "" });
        }
    };

    return (
        <div style={{ margin: "20px" }}>
            {/* Tabovi */}
            <CodebookTabs activeTab={activeTab} onClick={handleTabClick} />

            {activeTab === "Šifarnici" && renderSifarniciDropdown()}

            {activeTab === "Mjerna mjesta" && (
                <><EICInput eicCode={eicCode} setEicCode={setEicCode} onSearch={() => fetchDataByEIC("mjerna_mjesta")} />
                    <div className="eic-container">
                        <h3>Mjerna mjesta bez validne geometrije</h3>
                        <button className="generate-button" onClick={generateEICFileFromGeoServer}>
                            Generiši fajl
                        </button>
                    </div>
                </>
            )}
            {activeTab === "Očitanja" && (
                <EICInput eicCode={eicCode} setEicCode={setEicCode} onSearch={() => fetchDataByEIC("mjerna_mjesta_mjerenja")} />
            )}

            {activeTab === "Učitavanje" && (
                <div className="upload-container">
                    {/* Upload fajla */}
                    <div>
                        <label>Izaberite fajl za otpremanje:</label>
                        <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileChange} />
                        <button onClick={handleUploadClick} disabled={!file}>Učitaj podatke</button>
                    </div>

                    {/* Pokretanje parsera */}
                    <label>Izaberite fajl za parsiranje:</label>
                    <select
                        onFocus={() => fetchAvailableFiles(setAvailableFiles)}
                        onChange={(e) => setSelectedFile(e.target.value)}
                        value={selectedFile}>
                        <option value="">-- Izaberite fajl --</option>
                        {Array.isArray(availableFiles) && availableFiles.length > 0 ? (
                            availableFiles.map((file, index) => (
                                <option key={index} value={file}>{file}</option>
                            ))
                        ) : (
                            <option value="">Nema dostupnih fajlova</option>
                        )}

                    </select>
                    <button onClick={handleParseClick} disabled={!selectedFile}>
                        Pokreni parser
                    </button>
                </div>
            )}

            {activeTab === "Šifarnici" && tableData.length > 0 && (
                <div className="filter-container">
                    <input
                        type="text"
                        placeholder="Pretraži..."
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                    />
                    <select
                        value={selectedColumn}
                        onChange={(e) => setSelectedColumn(e.target.value)}
                        title="Izaberite kolonu"
                    >
                        <option value="">Sve kolone</option>
                        {tableData.length > 0 &&
                            Object.keys(tableData[0]).map((key) => (
                                <option className="options" key={key} value={key}>{key}</option>
                            ))}
                    </select>
                    {(searchKeyword || selectedColumn) && (
                        <button onClick={() => {
                            setSearchKeyword("");
                            setSelectedColumn("");
                        }}
                        className="buttonReset"
                        >
                            Poništi filter
                        </button>
                    )}
                </div>
            )}

            {activeTab === "Mjerna mjesta" && (
                <DataTable data={mjernaMjestaData} orientation="vertical" selectedFeature={selectedFeature} />
            )}

            {activeTab === "Očitanja" && (
                <DataTable data={mjernaMjestaMjerenjaData} orientation="vertical" />
            )}

            {activeTab === "Šifarnici" && (
                <div className="table-container">
                    <DataTable data={filteredData} orientation="horizontal" />
                </div>
            )}

        </div >
    );
}

export default Codebook;